import { round } from 'lodash';
import { colors } from '@ovpn-ui/styles';
import { appendScript } from '@ovpn-ui/utils';

import type { Boundaries } from '../types';

import { SubscriptionPlan, SubscriptionStatus, ProductType, Currency } from '../constants/enums';

export const convertDate = (date: Date | null) => {
    if (!date) return 'N/A';

    const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const day = date.getDate();

    return `${month} ${day}, ${year}`;
};

export const convertTimestamp = (timestampInSeconds: number | null) => {
    if (!timestampInSeconds) return 'N/A';

    const date = new Date(timestampInSeconds * 1000);
    return convertDate(date);
};

export const getStatusColor = (status: SubscriptionStatus, free: boolean) => {
    if (!status && free) {
        return colors.emptyStateBlue;
    }

    switch (status) {
        case SubscriptionStatus.Future:
            return colors.lightBlue;
        case SubscriptionStatus.PaymentDue:
            return colors.redHover;
        case SubscriptionStatus.Cancelled:
            return colors.secondGray;
        case SubscriptionStatus.Trial:
            return colors.dustyOrange;
        case SubscriptionStatus.NonRenewing:
        case SubscriptionStatus.Active:
            return colors.green;
        default:
            return colors.secondGray;
    }
};

export const getBoundaries = (
    pricing: {
        devices: number;
        free?: boolean;
        offline?: boolean;
        maxTrialDevices?: number;
        trialDays?: number;
        solo?: boolean;
        yearly?: boolean;
        monthly?: boolean;
        marketingMin?: boolean;
    }[],
) => {
    if (!pricing?.length) {
        return null;
    }

    const offline = pricing.find(t => t.offline)?.devices;
    const { maxTrialDevices, trialDays } = pricing.find(t => t.maxTrialDevices) || {};
    const trialOffline = maxTrialDevices ? maxTrialDevices + 1 : undefined;
    const min = pricing.find(t => t.marketingMin)?.devices || pricing.find(t => !t.solo)?.devices;
    const trialMin = pricing[1].devices;
    const max = offline! - 1;
    const free = pricing.find(t => t.free)?.devices;
    const monthlyOnly = pricing.filter(t => t.yearly === false).map(t => t.devices);
    const yearlyOnly = pricing.filter(t => t.monthly === false).map(t => t.devices);

    const prohibitedRanges: number[][] = [];
    for (let i = 0; i < pricing.length; i++) {
        if (pricing[i].solo) {
            prohibitedRanges.push([pricing[i].devices + 1, pricing[i + 1].devices - 1]);
        }
    }

    return {
        prohibitedRanges,
        offline,
        trialOffline,
        trialDays,
        free,
        max,
        min,
        trialMin,
        monthlyOnly,
        yearlyOnly,
    } as Boundaries;
};

export const getPricingMarks = (pricing: any) => {
    if (!pricing?.length) {
        return [];
    }

    const offline = Math.max(...pricing.map((t: any) => t.devices)) + 1;

    const options = [
        ...pricing
            .filter((p: any) => p.plan === SubscriptionPlan.Yearly)
            .map((p: any) => ({
                data: p.devices,
                label: p.devices,
            })),
        {
            data: offline,
            label: 'More',
        },
    ];

    const sumTo: any = (n: any) => (n === 0 ? 0 : n + sumTo(n - 1));

    return options.map((option, i) => {
        let steps: number | null = (options[i + 1]?.data as number) - option.data;
        let value = sumTo(i + 1) * 100;

        if (i === 0) {
            steps = 1;
        }

        if (i === options.length - 1) {
            value = sumTo(i) * 100 + 400;
            steps = null;
        }

        return {
            ...option,
            steps,
            value,
        };
    });
};

export const getPricePerUnit = (
    free: boolean,
    price: number,
    devicesCount: number,
    term: boolean,
) => {
    if (free) {
        return 0;
    }

    return term ? round(price / devicesCount / 12) : round(price / devicesCount);
};

export const formatPrice = (price: number) => {
    const wholePart = 3;
    const decimals = 2;

    const wpDelimiter = ',';
    const dDelimiter = '.';

    const re = `\\d(?=(\\d{${wholePart}})+\\${dDelimiter})`;

    const formattedPrice = parseFloat(price.toString())
        .toFixed(decimals)
        .replace(new RegExp(re, 'g'), `$&${wpDelimiter}`);

    return `${formattedPrice}`;
};

export const formatInput = (value: string | number) => {
    if (!value) {
        return undefined;
    }

    if (typeof value === 'number') {
        return value;
    }

    const trimmedValue = String(value).trim();

    return trimmedValue.length ? trimmedValue : undefined;
};

export const formatPhone = (value: string) => {
    if (!value) {
        return undefined;
    }

    if (/^\+?([0-9])*$/.test(value)) {
        return value;
    }

    const validValue = (value.match(/(?:^\+)|(?:\d)/g) || []).join('');

    return validValue.length >= 1 ? validValue : undefined;
};

export const formatFormValues = <T extends Record<string, any>>(data: T): Record<keyof T, any> =>
    Object.entries(data).reduce((obj, [key, value]) => {
        let formattedValue;

        if (Array.isArray(value)) {
            formattedValue = value.map(formatInput);
        } else if (typeof value === 'object') {
            formattedValue = formatFormValues(value);
        } else {
            formattedValue = formatInput(value);
        }

        return {
            ...obj,
            [key]: formattedValue,
        };
    }, {} as T);

export function saveLinkAsFile(blob: Blob, name: string) {
    // IE
    if (window.navigator && (window.navigator as any).msSaveBlob) {
        (window.navigator as any).msSaveBlob(blob, name);

        return;
    }

    // Chrome on iOS
    if (/CriOS\/[\d]+/.test(navigator.userAgent)) {
        const reader = new FileReader();

        reader.onloadend = () => {
            const a = document.createElement('a');

            a.href = typeof reader.result === 'string' ? reader.result : '';
            a.style.display = 'none';
            a.download = name;
            document.body.appendChild(a);
            a.click();
            a?.parentNode?.removeChild(a);
        };

        reader.readAsDataURL(blob);

        return;
    }

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);

    if (name) {
        link.download = name;
    }

    link.dispatchEvent(
        new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
        }),
    );
}

export const isValidUrl = (url: string) => {
    try {
        return Boolean(new URL(url));
    } catch {
        return false;
    }
};

export function detectSubscriptionChanges(
    values: { devices: number; term?: SubscriptionPlan; payment?: string },
    initialValues: { devices: number; term: { value: string }; payment?: { value: string } },
) {
    return {
        changePrice:
            +values.devices !== +initialValues.devices ||
            (values.term !== initialValues.term?.value && initialValues.term?.value),
        changePayment: values.payment !== initialValues?.payment?.value,
    };
}

export function isValidDevicesParam(
    boundaries: Boundaries,
    devices: string | boolean | undefined,
): devices is string {
    if (!devices) {
        return false;
    }

    if (
        !+devices ||
        +devices % 1 !== 0 ||
        /[.,]/.test(devices.toString()) ||
        +devices > boundaries.max ||
        +devices < boundaries.free
    ) {
        return false;
    }

    const prohibited = boundaries?.prohibitedRanges?.find(
        range => +devices >= range[0] && +devices <= range[1],
    );

    return !prohibited;
}

export function isValidCurrencyParam(currency: string | boolean | undefined): currency is string {
    if (typeof currency !== 'string') {
        return false;
    }

    return !!Object.values(Currency).find(curr => curr.toLowerCase() === currency);
}

export function isValidTermParam(term: string | boolean | undefined): term is string {
    return !!term && (term === 'monthly' || term === 'yearly');
}

export function getDowngradeNotification<
    T extends { future?: boolean; paymentDue?: boolean; trial?: boolean },
>(type: T) {
    return type.future || type.paymentDue || type.trial
        ? 'Success! Subscription was cancelled'
        : 'Success! Subscription cancellation is scheduled end of term';
}

export const loadRuntimeVariables = (onload: (value?: unknown) => void) => {
    if (!window._billing_env_) {
        appendScript({
            src: `${window._env_.BILLING_UI || window._env_.UI_BILLING_HOST}/static/env-vars.js`,
            onload,
        } as React.ScriptHTMLAttributes<HTMLScriptElement>);
    } else {
        onload();
    }
};

export const delay = (ms: number) =>
    new Promise<undefined>(resolve => {
        setTimeout(resolve, ms);
    });

export function getEnvProduct(): ProductType | undefined;
export function getEnvProduct(match: ProductType): boolean | undefined;
export function getEnvProduct(match?: ProductType) {
    if (!window._env_) {
        return undefined;
    }

    const product = window._env_.APP_NAME === 'cloud-ui-root' ? ProductType.Cloud : ProductType.As;

    return match ? match === product : product;
}

export const product = getEnvProduct()!;

export const isProd = ['blue', 'green'].includes(window._env_.CLUSTER_NAME);
